/*
  Collection-first checkout (added Oct 2026).

  Shopify checkout opens on "Ship" by default, and on the Basic plan we can't
  change that inside checkout. What we can do is tell Shopify, before checkout
  starts, which delivery method this basket prefers. We save that on the cart
  through the Storefront API (cartBuyerIdentityUpdate, delivery preferences),
  and checkout then opens on Pickup with our shop already selected.

  - The choice ("collect" or "deliver") is made on the basket page and kept in
    this browser. Everyone starts on collect.
  - Deliver is only sent when the basket meets the local delivery minimum;
    below it we always send collect, so nobody lands on "Shipping not available".
  - The preference is saved whenever the basket changes, so a checkout press
    normally goes straight through. If it hasn't been saved yet, the press
    waits for it (at most 2 seconds) and then carries on as normal.

  Safety: everything is best effort. If the API call fails or is slow, checkout
  opens exactly as it did before (on Ship). Nothing here ever blocks checkout.
  Settings: the "Collection-first checkout" section in the footer.
*/
(function () {
  if (window.rrCollectPref) return;

  var cfg = {};
  try {
    var cfgEl = document.querySelector('script[data-rr-collect-pref-config]');
    if (cfgEl) cfg = JSON.parse(cfgEl.textContent);
  } catch (e) { /* defaults below */ }

  var LOCATION_ID = String(cfg.locationId || '').replace(/\D/g, '');
  var MINIMUM = Math.round(parseFloat(cfg.minimum || 20) * 100);   // pence
  var API_URL = (cfg.root || '/').replace(/\/?$/, '/') + 'api/2025-10/graphql.json';
  var WAIT_MS = 2000;
  var MODE_KEY = 'rr_fulfilment';
  var SAVED_KEY = 'rr_fulfilment_saved';
  var MUTATION = 'mutation($id:ID!,$b:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$id,buyerIdentity:$b){cart{id} userErrors{message}}}';

  function local(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) { return null; }
  }
  function session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) { return null; }
  }

  function getMode() { return local(MODE_KEY) === 'deliver' ? 'deliver' : 'collect'; }

  // True until the current basket + choice has been saved on the cart.
  var dirty = true;
  var running = null;
  var changes = 0;        // bumps on every basket change, so a slow save can't mark a newer change as saved

  function save() {
    if (running) return running;
    var seen = changes;
    function settled() { if (seen === changes) dirty = false; }
    running = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || !cart.token || !cart.item_count) { settled(); return false; }
        var mode = getMode();
        if (mode === 'deliver' && cart.total_price < MINIMUM) mode = 'collect';
        var signature = cart.token + '|' + mode;
        if (session(SAVED_KEY) === signature) { settled(); return true; }

        var delivery = { deliveryMethod: ['SHIPPING'] };
        if (mode === 'collect') {
          delivery = { deliveryMethod: ['PICK_UP'] };
          if (LOCATION_ID) delivery.pickupHandle = [LOCATION_ID];
        }
        return fetch(API_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            query: MUTATION,
            variables: {
              id: 'gid://shopify/Cart/' + cart.token,
              b: { countryCode: 'GB', preferences: { delivery: delivery } }
            }
          })
        })
          .then(function (res) { return res.json(); })
          .then(function (body) {
            var result = body && body.data && body.data.cartBuyerIdentityUpdate;
            var ok = !!(result && result.cart && !(result.userErrors && result.userErrors.length));
            if (ok) { session(SAVED_KEY, signature); settled(); }
            return ok;
          });
      })
      ['catch'](function () { return false; })
      .then(function (ok) { running = null; return ok; });
    return running;
  }

  var timer = null;
  function saveSoon() {
    dirty = true;
    changes++;
    clearTimeout(timer);
    timer = setTimeout(function () {
      (running || Promise.resolve()).then(save);
    }, 400);
  }

  // Any basket change (add, quantity, remove) re-saves the preference, because
  // the delivery minimum may have been crossed or a new cart started. The theme
  // uses both fetch and jQuery (XMLHttpRequest) for these, so both are watched.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?/;
  try {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__rrCartWrite = String(method).toUpperCase() === 'POST' && CART_WRITE.test(String(url)); } catch (e) {}
      return originalOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try { if (this.__rrCartWrite) this.addEventListener('loadend', saveSoon); } catch (e) {}
      return originalSend.apply(this, arguments);
    };
  } catch (e) { /* the checkout press below still saves before leaving */ }
  try {
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var result = originalFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && CART_WRITE.test(url)) {
          result.then(saveSoon, function () {});
        }
      } catch (e) { /* never interfere with the request */ }
      return result;
    };
  } catch (e) { /* the checkout press below still saves before leaving */ }
  document.addEventListener('cart:refresh', saveSoon);

  // Checkout presses: the basket page button, the add-to-cart pop-up button,
  // and any plain link to /checkout. Runs first (capture), and only holds the
  // press when the preference hasn't been saved yet.
  var bypass = false;
  var waiting = false;
  document.addEventListener('click', function (evt) {
    if (bypass) return;
    var el = evt.target && evt.target.closest
      ? evt.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]')
      : null;
    if (!el) return;
    if (!dirty && !running) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();
    if (waiting) return;
    waiting = true;

    var done = false;
    function proceed() {
      if (done) return;
      done = true;
      waiting = false;
      bypass = true;
      try { el.click(); } finally { bypass = false; }
    }
    setTimeout(proceed, WAIT_MS);
    (running || Promise.resolve()).then(save).then(proceed, proceed);
  }, true);

  window.rrCollectPref = {
    minimum: MINIMUM,
    getMode: getMode,
    setMode: function (mode) {
      local(MODE_KEY, mode === 'deliver' ? 'deliver' : 'collect');
      dirty = true;
      changes++;
      return (running || Promise.resolve()).then(save);
    },
    save: save
  };

  if (/(^|;\s*)cart=/.test(document.cookie)) save();
}());
